import {useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {apiErrorCodes, useApiMutation} from '@legit.health/backend-client';
import {
  generatePath,
  BodySite,
  BodySiteCode,
  AnonymousDiagnosticReport,
  generateRandomId,
  ImageValue,
  LocaleCode,
  AnamnesisQuestion
} from '@legit.health/ui';
import {Box} from '@mui/material';
import * as Sentry from '@sentry/react';
import {TFunction} from 'i18next';
import {useForm} from 'react-hook-form';
import {useParamsContext} from '@/contexts/ParamsContext';
import postImageRejectedMessage from '@/utils/postImageRejectedMessage';
import AnamnesisGeneralField from './components/AnamnesisGeneralField/AnamnesisGeneralField';
import BodySiteField from './components/BodySiteField/BodySiteField';
import ErrorDialog from './components/ErrorDialog/ErrorDialog';
import UploadImages from './components/UploadImages';
import FormValues from './interfaces/FormValues';
import createSchema from './utils/createSchema';
import mapFormValuesToApiFormat from './utils/mapFormValuesToApiFormat';

export default function InitialForm({
  bodySites,
  t,
  locale,
  onSuccessfullResponse,
  anamnesisQuestions
}: {
  bodySites: BodySite[];
  t: TFunction;
  locale: LocaleCode;
  onSuccessfullResponse: (data: {
    anamnesisQuestions: Record<string, string>;
    diagnosticReport: AnonymousDiagnosticReport;
    images: ImageValue[];
    bodySiteCode: BodySiteCode;
    visitIdentifier: string;
  }) => void;
  anamnesisQuestions: AnamnesisQuestion[];
}) {
  const [shouldShowDialog, setShouldShowDialog] = useState<0 | 1 | 2>(0);
  const {
    extraData,
    companyCallbackUrl,
    enableAnamnesis,
    macroscopicMedia: macroscopicMediaParam,
    visitIdentifier
  } = useParamsContext();

  const {
    mutate,
    isPending,
    isSuccess,
    error: apiError
  } = useApiMutation<AnonymousDiagnosticReport>();
  const form = useForm<FormValues>({
    resolver: yupResolver(createSchema(macroscopicMediaParam)),
    defaultValues: {
      images: [
        {
          id: generateRandomId(),
          file: null,
          crop: null
        }
      ],
      bodySiteCode: '',
      lesionType: null,
      visitIdentifier,
      anamnesisQuestions: anamnesisQuestions.reduce(
        (acc, aq) => ({
          ...acc,
          [aq.id]: ''
        }),
        {}
      ),
      macroscopicMedia: null,
      extraData,
      companyCallbackUrl
    },
    shouldFocusError: false
  });

  async function handleSubmit(formValues: FormValues) {
    const values = await mapFormValuesToApiFormat(formValues);
    mutate(
      {
        path: generatePath('/diagnosis-support', null, {
          locale
        }),
        method: 'POST',
        body: values
      },
      {
        onSuccess(response) {
          onSuccessfullResponse({
            diagnosticReport: response.data,
            images: formValues.images,
            bodySiteCode: formValues.bodySiteCode,
            visitIdentifier: formValues.visitIdentifier,
            anamnesisQuestions: formValues.anamnesisQuestions
          });
        },
        onError(apiError) {
          const isImageError =
            apiError.content.errorCode === apiErrorCodes.medicalDevice.invalidImage;
          if (isImageError) {
            postImageRejectedMessage(apiError.content.data);
          } else {
            Sentry.captureException(apiError, {
              extra: {
                filenames: formValues.images.map((i) => ({
                  type: i.file?.type,
                  size: i.file?.size,
                  cropDimensions: i.crop
                }))
              }
            });
          }
          setShouldShowDialog(isImageError ? 1 : 2);
          form.setValue(
            'images',
            formValues.images.filter((i) => i.file)
          );
        }
      }
    );
  }

  function closeDialog() {
    setShouldShowDialog(0);
  }

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} data-testid="diagnosticReportForm">
      <div>
        {enableAnamnesis && (
          <AnamnesisGeneralField t={t} form={form} anamnesisQuestions={anamnesisQuestions} />
        )}
        <Box mb={4}>
          <BodySiteField form={form} bodySites={bodySites} t={t} />
        </Box>
        <Box mb={4}>
          <UploadImages t={t} form={form} isPending={isPending} isSuccess={isSuccess} />
        </Box>
        <ErrorDialog
          form={form}
          apiError={apiError}
          t={t}
          shouldShowDialog={shouldShowDialog}
          closeDialog={closeDialog}
        />
      </div>
    </form>
  );
}
